import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AppService } from '../app.service';
import { ApiRequestService } from '../shared/api-request.service';
import { UtilsService } from '../shared/utils.service';
import { MatDialogRef } from '@angular/material/dialog';
import { AccountModel } from '../models/account.model';
import { GlobalAccountsSelectorComponent } from '../shared/global-accounts-selector/global-accounts-selector.component';
import { Router } from '@angular/router';

@Component({
  selector: 'app-default-account',
  templateUrl: './default-account.component.html',
  styleUrls: ['./default-account.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class DefaultAccountComponent implements OnInit {
  /**
   * Is the default account currently being set?
   */
  inProgress = false;

  /**
   * The default account for the authenticated user.
   */
  defaultAccount = new AccountModel();

  // Used to determine if the user already triggered an account update or not.
  alreadyUpdated: boolean = false;

  constructor(
    public app: AppService,
    private api: ApiRequestService,
    private utils: UtilsService,
    private dialogRef: MatDialogRef<DefaultAccountComponent>,
    private router: Router
  ) {
    // Disable external close options.
    dialogRef.disableClose = true;
  }

  public ngOnInit(): void {
    if (!this.app.user.account_id) {
      return;
    }
    // Get the user's default account.
    this.getDefaultAccount();
  }

  /**
   * Close the dialog.
   */
  public onClose(): void {
    // Check if the user already updated their account.
    if (this.alreadyUpdated) {
      // Close the dialog.
      this.dialogRef.close();
      return;
    }

    this.inProgress = true;
    // Send a request to update the user's default account.
    this.api
      .makeRequest('put', 'v2/user/employer', {
        account_id: this.defaultAccount.id,
        search: null
      })
      .then((response) => {
        // Close the dialog.
        this.dialogRef.close();
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      })
      .finally(() => (this.inProgress = false));
  }

  /**
   * Open a dialog to allow the user to select or enter an employer name.
   */
  public onSelectAccount(): void {
    this.utils.showComponentDialog(
      GlobalAccountsSelectorComponent,
      {
        selected: [this.defaultAccount.id],
        multiple: false,
        // Not pre-seeded with the current employer (SC-301). Seeding a personal account's name produced a
        // search that could never match, and seeding a real one listed that account twice. The dialog now
        // lists the user's own accounts up front instead.
        search: ''
      },
      {
        width: '1024px'
      },
      (response: number | string) => {
        // Terminate the request if the dialog was closed with selecting or entering a value.
        if (typeof response == 'undefined') {
          return;
        }
        // Do final confirmation.
        this.utils.showModal(
          'Confirmation Required',
          'Are you sure you want to change your employer?',
          () => {
            // Make a request to update the user's employer.
            this.api
              .makeRequest('put', 'v2/user/employer', {
                account_id: typeof response === 'number' ? response : null,
                search: typeof response !== 'number' ? response : null
              })
              .then((subResponse) => {
                // The employer no longer always changes immediately (SC-301). When the account already
                // existed, this became a request that an administrator has to approve, and the user's
                // employer stays where it was. Reporting success here would tell them the change had been
                // made when it had not, and they would then go looking for data they cannot yet see.
                if (subResponse?.pending_approval) {
                  this.utils.showToast(
                    subResponse.message ||
                      'Your request to join ' +
                        (subResponse.account_name || 'the account') +
                        ' has been sent for approval.'
                  );
                  this.dialogRef.close();
                  this.router.navigate(['/user/account-access']);
                  return;
                }

                this.utils.showToast('Your employer was successfully changed.');
                this.alreadyUpdated = true;
                // The user's employer is now the new account, so the employer endpoint returns it.
                this.getDefaultAccount();
              })
              .catch((errorResponse) => {
                this.utils.handleAPIErrors(errorResponse);
              });
          }
        );
      }
    );
  }

  /**
   * Get the user's default account (their employer).
   *
   * RBAC: this used to read v2/accounts/{id}, which is the account settings read and requires
   * account-settings:edit. Every user sees this dialog after sign in, so users without that permission
   * (for example a user with only the core user profile permission) got a 403, an empty Company Name,
   * and a Save that posted no employer id. v2/user/employer returns the user's own employer under the
   * core user profile permission, the same source UserDetailsComponent.getEmployer() uses.
   * @private
   */
  private getDefaultAccount() {
    return this.api
      .makeRequest('get', 'v2/user/employer')
      .then((account: AccountModel) => {
        // The API answers an empty body when the employer account no longer exists.
        if (!account) {
          return;
        }
        // Store the default account details.
        this.defaultAccount.apply(account);
        // Set the new account id.
        this.app.user.account_id = this.defaultAccount.id;
      })
      .catch((): void => {
        // Leave the default account empty. Save stays disabled without an employer id, and the user can
        // still pick an employer through Change Employer.
      });
  }
}
